/**
 * @fileoverview Zod schemas and types for Payments (Universal Transactions view).
 * @module features/payments/schemas/paymentsSchema
 */

import { z } from 'zod'

/**
 * Currency object schema for nested currency details.
 */
const currencyObjectSchema = z
  .object({
    currencyCode: z.string(),
  })
  .passthrough()

/**
 * Raw payment shape returned by the universal transactions endpoint.
 */
export const paymentRawSchema = z
  .object({
    id: z.string(),
    txnDate: z.string(),
    reportingAmount: z.number().nullable().optional(),
    reportingSignedAmount: z.number().nullable().optional(),
    reportingCurrency: z.union([z.string(), currencyObjectSchema]).nullable().optional(),
    originalAmount: z.number().nullable().optional(),
    originalSignedAmount: z.number().nullable().optional(),
    originalCurrency: z.union([z.string(), currencyObjectSchema]).nullable().optional(),
    direction: z.string(),
    title: z.string(),
    counterpartyName: z.string().nullable().optional(),
    extras: z.record(z.string(), z.unknown()).optional(),
    status: z.string().nullable().optional(),
    accountIban: z.string().nullable().optional(),
    reviewed: z.boolean().optional(),
    notes: z.string().nullable().optional(),
    invoiceStatus: z.string().nullable().optional(),
    vatApplicable: z.boolean().nullable().optional(),
    reverseCharge: z.boolean().nullable().optional(),
    vatRate: z.number().nullable().optional(),
    createdAt: z.string(),
    // Newly provided by BE: indicates whether linking to an indirect cost is required
    costLinkingRequirement: z.enum(['REQUIRED', 'NOT_NEEDED', 'OPTIONAL']).optional(),
    // NEW computed fields from backend (read-only)
    linkedIndirectCostCount: z.number().int().nonnegative().optional(),
    linkingRequirementSatisfied: z.boolean().optional(),
    linkedSubcategories: z
      .array(z.object({ costUuid: z.string(), displayName: z.string() }))
      .optional(),
  })
  .passthrough()
  .refine(
    raw => raw.reportingAmount != null || raw.originalAmount != null,
    'Payment requires reportingAmount or originalAmount'
  )
  .refine(
    raw => raw.reportingCurrency != null || raw.originalCurrency != null,
    'Payment requires reportingCurrency or originalCurrency'
  )
  .refine(
    raw => raw.reportingSignedAmount != null || raw.originalSignedAmount != null,
    'Payment requires reportingSignedAmount or originalSignedAmount'
  )
  .refine(raw => raw.direction.length > 0, 'Payment direction is required')

/**
 * Raw payment type derived from backend payload.
 */
export type PaymentRaw = z.infer<typeof paymentRawSchema>

/**
 * Canonical payment item derived from Universal Transactions.
 */
export const paymentSchema = z.object({
  id: z.string(),
  txnDate: z.string(),
  amount: z.number(),
  signedAmount: z.number(),
  direction: z.enum(['DEBIT', 'CREDIT']),
  currency: z.string(),
  title: z.string(),
  counterpartyName: z.string().nullable().optional(),
  vatApplicable: z.boolean().nullable(),
  reverseCharge: z.boolean(),
  vatRate: z.number().nullable(),
  invoiceStatus: z
    .enum(['NO_INFO', 'INVOICE_PRESENT', 'INVOICE_NOT_YET_AVAILABLE', 'INVOICE_NOT_NEEDED'])
    .nullable()
    .optional(),
  // Mirrors BE field for UI cues and actions
  costLinkingRequirement: z.enum(['REQUIRED', 'NOT_NEEDED', 'OPTIONAL']).optional(),
  // Computed fields surfaced by the list endpoint
  linkedIndirectCostCount: z.number().int().nonnegative().default(0),
  linkingRequirementSatisfied: z.boolean().default(false),
  linkedSubcategories: z
    .array(z.object({ costUuid: z.string(), displayName: z.string() }))
    .default([]),
})
export type PaymentDTO = z.infer<typeof paymentSchema>

/**
 * Paginated payments response after normalization.
 */
export interface PaymentsPageResponse {
  content: PaymentDTO[]
  totalPages?: number | undefined
  totalElements?: number | undefined
  size?: number | undefined
  number?: number | undefined
  last?: boolean | undefined
}

/**
 * Helper to extract currency code string from string or object.
 */
function extractCurrencyCode(val: string | { currencyCode: string } | null | undefined): string | null | undefined {
  if (!val) return val
  if (typeof val === 'string') return val
  return val.currencyCode
}

/**
 * Normalize a raw payment into canonical PaymentDTO shape.
 *
 * @param raw Raw payment payload from backend
 * @returns Canonical payment record
 * @example
 * ```ts
 * const normalized = normalizePayment(parsedRaw)
 * ```
 */
export function normalizePayment(raw: PaymentRaw): PaymentDTO {
  const direction = raw.direction.toUpperCase() === 'CREDIT' ? 'CREDIT' : 'DEBIT'
  
  const rawReporting = extractCurrencyCode(raw.reportingCurrency)
  const rawOriginal = extractCurrencyCode(raw.originalCurrency)
  const currency = (rawReporting ?? rawOriginal) as string

  const amount = raw.reportingAmount ?? raw.originalAmount
  const signedAmount = raw.reportingSignedAmount ?? raw.originalSignedAmount
  const vatApplicable = raw.vatApplicable === null ? null : Boolean(raw.vatApplicable)
  const reverseCharge = Boolean(raw.reverseCharge)
  const vatRate = typeof raw.vatRate === 'number' ? raw.vatRate : null

  return paymentSchema.parse({
    id: raw.id,
    txnDate: raw.txnDate,
    amount,
    signedAmount,
    direction,
    currency,
    title: raw.title,
    counterpartyName: raw.counterpartyName ?? undefined,
    vatApplicable,
    reverseCharge,
    vatRate,
    invoiceStatus: raw.invoiceStatus ?? undefined,
    costLinkingRequirement: raw.costLinkingRequirement ?? undefined,
    linkedIndirectCostCount: raw.linkedIndirectCostCount ?? 0,
    linkingRequirementSatisfied: raw.linkingRequirementSatisfied ?? false,
    linkedSubcategories: raw.linkedSubcategories ?? [],
  })
}

/** Spring-like page metadata minimal shape (raw). */
const pageMetadataSchema = z
  .object({
    size: z.number(),
    number: z.number(),
    totalElements: z.number(),
    totalPages: z.number(),
  })
  .passthrough()

const paymentPageRawSchema = z.object({
  content: z.array(paymentRawSchema).default([]),
  // Support both old (flat) and new (nested page) structure
  totalPages: z.number().optional(),
  totalElements: z.number().optional(),
  size: z.number().optional(),
  number: z.number().optional(),
  last: z.boolean().optional(),
  page: pageMetadataSchema.optional(),
})

/** Type for raw page payload. */
export type PaymentPageRaw = z.infer<typeof paymentPageRawSchema>

/**
 * Normalize page metadata and rows.
 *
 * @param raw Raw page payload from backend
 * @returns Normalized page response
 * @example
 * ```ts
 * const normalized = normalizePaymentsPage(rawPage)
 * ```
 */
export function normalizePaymentsPage(raw: PaymentPageRaw): PaymentsPageResponse {
  let meta: Partial<PaymentsPageResponse> = {}
  
  if (raw.page) {
    meta = {
      size: raw.page.size,
      number: raw.page.number,
      totalElements: raw.page.totalElements,
      totalPages: raw.page.totalPages,
      last: raw.page.number + 1 >= raw.page.totalPages,
    }
  } else {
    meta = {
      size: raw.size,
      number: raw.number,
      totalElements: raw.totalElements,
      totalPages: raw.totalPages,
      last: raw.last,
    }
  }

  return {
    content: raw.content.map(normalizePayment),
    ...meta,
  }
}

/**
 * Page schema that parses raw payload and returns normalized payments.
 */
export const pageSchema = paymentPageRawSchema.transform(normalizePaymentsPage)